/**
 * Order Modification dialog (spec Part 11/12) — a two-step Preview ->
 * Confirm flow mirroring Phase 4's own Execution Preview/Confirm pattern.
 *
 * EXECUTION SAFETY: opening this dialog only calls
 * `positionService.previewModifyOrder` (read-only — re-fetches the
 * broker's own current order status, never modifies anything). The
 * [Confirm Modification] button is the ONLY control that calls
 * `positionService.modifyOrder`, and it stays disabled until the preview
 * has loaded and confirms the order is still modifiable.
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import positionService from "@/services/positionService";
import type { ModifyOrderPreviewRead, OrderMonitorRead } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { fmtCurrency } from "@/pages/strategy-adjustment/constants";

export function ModifyOrderDialog({
  order,
  open,
  onClose,
  onModified,
}: {
  order: OrderMonitorRead | null;
  open: boolean;
  onClose: () => void;
  onModified: () => void;
}) {
  const [preview, setPreview] = useState<ModifyOrderPreviewRead | null>(null);
  const [price, setPrice] = useState<string>("");
  const [triggerPrice, setTriggerPrice] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !order) return;
    setError(null);
    setLoading(true);
    positionService
      .previewModifyOrder(order.order_id, {})
      .then((result) => {
        setPreview(result);
        setPrice(result.current_price != null ? String(result.current_price) : "");
        setTriggerPrice(result.current_trigger_price != null ? String(result.current_trigger_price) : "");
        setQuantity(String(result.current_quantity));
      })
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [open, order]);

  const handleClose = () => {
    setPreview(null);
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!order) return;
    setSubmitting(true);
    setError(null);
    try {
      await positionService.modifyOrder(order.order_id, {
        price: price ? Number(price) : null,
        trigger_price: triggerPrice ? Number(triggerPrice) : null,
        quantity: quantity ? Number(quantity) : null,
        acknowledged: true,
      });
      onModified();
      handleClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Modify Order</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {order && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {order.side} {order.underlying} {String(order.strike)} {order.option_type} — {order.quantity} qty
          </Typography>
        )}
        {loading && <Typography variant="body2">Fetching the latest status from the broker…</Typography>}
        {preview && !preview.modifiable && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {preview.block_reason ?? "This order can no longer be modified."}
          </Alert>
        )}
        {preview && preview.modifiable && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Current: {preview.current_status} · {fmtCurrency(preview.current_price)}
              {preview.current_trigger_price != null && ` · Trigger ${fmtCurrency(preview.current_trigger_price)}`}
            </Typography>
            <Divider />
            <TextField label="New Quantity (lots)" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} fullWidth />
            <TextField label="New Limit Price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} fullWidth />
            <TextField
              label="New Trigger Price"
              type="number"
              value={triggerPrice}
              onChange={(e) => setTriggerPrice(e.target.value)}
              fullWidth
              helperText="Only applies to SL_LIMIT orders."
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!preview || !preview.modifiable || submitting}
          onClick={handleConfirm}
        >
          Confirm Modification
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ModifyOrderDialog;
